import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listAllOrganizations } from '../../api/orgs';
import { formatDateTime } from '../../utils/format';
import Spinner from '../../components/ui/Spinner';

function normalizeList(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.content)) return d.content;
  if (Array.isArray(d?.organizations)) return d.organizations;
  return [];
}

/**
 * SUPER_ADMIN: all organizations (GET /api/orgs).
 * Row links to /interviews scoped to that org.
 */
export default function AllOrgsPage() {
  const [orgs, setOrgs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError('');
      setLoading(true);
      try {
        const res = await listAllOrganizations();
        if (cancelled) return;
        setOrgs(normalizeList(res));
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Failed to load organizations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="page-pad page-y flex justify-center py-24">
        <Spinner size="md" label="Loading organizations…" />
      </div>
    );
  }

  if (error) {
    return <div className="page-pad page-y text-sm text-red-600">{error}</div>;
  }

  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full space-y-6">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Platform admin</p>
        <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">All organizations</h1>
        <p className="text-sm text-ink-faint mt-0.5">Browse every org and open its interview activity.</p>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-canvas border-b border-line">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wider">Slug</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-ink-faint uppercase tracking-wider">Members</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-ink-faint uppercase tracking-wider">Sessions</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-faint uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-ink-faint uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-soft">
              {orgs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-ink-faint text-sm">No organizations found.</td>
                </tr>
              ) : (
                orgs.map((org) => {
                  const id = org.id ?? org.orgId;
                  const memberCount = org.memberCount ?? org.membersCount ?? org.member_count ?? '—';
                  const sessionCount = org.sessionCount ?? org.sessionsCount ?? org.session_count ?? '—';
                  const created = org.createdAt ?? org.created_at ?? org.created;
                  return (
                    <tr key={id || org.slug} className="hover:bg-teal-50/20 transition-colors">
                      <td className="px-4 py-3 font-semibold text-ink">{org.name || '—'}</td>
                      <td className="px-4 py-3 text-ink-soft font-mono text-xs">{org.slug || '—'}</td>
                      <td className="px-4 py-3 text-right font-mono text-ink-soft">{memberCount}</td>
                      <td className="px-4 py-3 text-right font-mono text-ink-soft">{sessionCount}</td>
                      <td className="px-4 py-3 text-ink-faint font-mono text-xs whitespace-nowrap">
                        {created ? formatDateTime(created) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {id ? (
                          <Link
                            to={`/interviews?orgId=${encodeURIComponent(id)}`}
                            className="text-sm font-semibold text-teal-600 hover:text-teal-700"
                          >
                            View sessions →
                          </Link>
                        ) : (
                          <span className="text-ink-faint text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
